import { FieldError, FieldErrors, UseFormRegister } from 'react-hook-form';

import React, { useEffect, useRef, useState } from 'react';

import arrowIcon from '@/assets/icons/arrow.svg';
import ChevronDown from '@/assets/icons/chevron-down.svg';
import Checkbox from './Checkbox';

type FormFieldError = FieldError | undefined | FieldErrors<any>;

interface Option {
  value: string;
  label: string | React.ReactNode;
  title?: string;
}

type SelectVariant = 'default' | 'icon-only' | 'text';

interface SelectFieldProps {
  name: string;
  label?: string | React.ReactNode;
  description?: string;
  placeholder?: string;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  disabled?: boolean;
  register?: UseFormRegister<any>;
  error?: FormFieldError;
  className?: string;
  options: Option[];
  variant?: SelectVariant;
  buttonClassName?: string;
  dropdownClassName?: string;
  size?: string;
  multiple?: boolean;
  title?: string; // for multiple select
}

const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label = '',
  description,
  placeholder = 'Select an option',
  value,
  onChange,
  disabled = false,
  register,
  error,
  className,
  options,
  variant = 'default',
  buttonClassName,
  dropdownClassName,
  size = '',
  multiple = false,
  title = 'options'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const registerProps = register ? register(name) : null;

  // Ensure value is always an array for multi-select, string for single-select
  const normalizedValue = multiple
    ? Array.isArray(value)
      ? value
      : value
        ? [value]
        : []
    : Array.isArray(value)
      ? value[0] || ''
      : value || '';

  const handleSelect = (selectedValue: string) => {
    let newValue: string | string[];

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : value ? [value] : [];
      if (currentValues.includes(selectedValue)) {
        // Remove from selection
        newValue = currentValues.filter((v) => v !== selectedValue);
      } else {
        // Add to selection
        newValue = [...currentValues, selectedValue];
      }
    } else {
      newValue = selectedValue;
      setIsOpen(false); // Close dropdown for single select
    }

    if (registerProps) {
      registerProps.onChange({
        target: { value: newValue, name }
      });
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  const errorMessage = error && 'message' in error ? error.message : '';
  const selectedOption = multiple ? null : options.find((opt) => opt.value === normalizedValue);
  const selectedOptions = multiple ? options.filter((opt) => (normalizedValue as string[]).includes(opt.value)) : [];

  // Get display text for the button
  const displayText = multiple
    ? selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
        ? selectedOptions[0].title
        : `${selectedOptions.length} ${title} selected`
    : (selectedOption?.label ?? placeholder);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`${className ? className : ''} relative`} ref={selectRef}>
      {label && (
        <label
          className={`inline-block text-[#495057] font-medium text-xs ${size === 'sm' ? 'md:mb-[0.45rem] md:text-sm' : 'md:text-lg mb-2'}`}
        >
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          ${
            variant === 'text'
              ? 'bg-transparent border-none py-2 px-3 gap-3'
              : `bg-lightGray rounded-[20px] border w-full
               ${variant === 'default' ? `p-[0.585rem] ${size === 'sm' ? 'md:p-[0.45rem] md:px-[0.875rem]' : 'md:p-[1.215rem]'} px-4` : 'p-2 md:p-3'}
               ${variant === 'icon-only' ? 'min-w-[44px] min-h-[44px] rounded-full' : ''}
               ${error ? 'border-red-500' : 'hover:border-[#ADB5BD]'}`
          }
          font-medium ${size === 'sm' ? 'text-sm md:text-base' : 'text-sm md:text-base'} flex items-center
          ${!selectedOption ? 'text-[#93999F]' : 'text-[#495057]'}
          ${variant === 'default' || variant === 'text' ? 'justify-between' : 'justify-center'}
          ${disabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}
          ${buttonClassName ? buttonClassName : ''}
        `}
      >
        {(variant === 'default' || variant === 'text') && (
          <span
            className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
            title={typeof displayText === 'string' ? displayText : undefined}
          >
            {displayText}
          </span>
        )}
        {variant === 'text' ? (
          <img
            className={`transform w-3 transition-transform duration-200 rotate-0 ${isOpen ? '!rotate-180' : ''}}`}
            src={ChevronDown}
            alt="arrow-icon"
          />
        ) : (
          <img
            className={`transform transition-transform duration-200 rotate-180 ${isOpen ? 'rotate-0' : ''}}`}
            src={arrowIcon}
            alt="arrow-icon"
          />
        )}
      </button>

      {isOpen && (
        <div
          className={`absolute z-10 mt-1 bg-white border rounded-lg shadow-lg right-0
    ${variant === 'default' ? 'w-full' : (dropdownClassName ?? 'min-w-[140px] w-max')}
  `}
        >
          <div className="py-1 max-h-60 overflow-auto">
            {options.map((option) => {
              const isSelected = multiple
                ? (normalizedValue as string[]).includes(option.value)
                : option.value === normalizedValue;

              return (
                <div
                  key={option.value}
                  className={`w-full px-4 py-2 cursor-pointer hover:bg-lightGray
                    ${multiple ? 'flex items-center gap-2' : 'text-left'}
                  `}
                  onClick={() => handleSelect(option.value)}
                >
                  {multiple ? (
                    <>
                      <Checkbox
                        name={`${name}-${option.value}`}
                        checked={isSelected}
                        onChange={() => {}} // Handled by parent click
                        className="pointer-events-none"
                      />
                      <span className="text-sm md:text-base text-[#495057] flex-1 pointer-events-none select-none">
                        {option.label}
                      </span>
                    </>
                  ) : (
                    <span
                      className={`text-sm md:text-base select-none whitespace-nowrap
                      ${isSelected ? 'text-primary font-medium' : 'text-[#495057]'}
                    `}
                    >
                      {option.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(errorMessage || description) && (
        <p className={`mt-2 text-xs ${errorMessage ? 'text-red-600' : 'text-[#93999F]'}`}>
          {(errorMessage as String) || description}
        </p>
      )}

      {register && (
        <input
          type="hidden"
          name={name}
          value={multiple ? JSON.stringify(normalizedValue) : (normalizedValue as string)}
          {...registerProps}
          onChange={(e) => {
            const newValue = multiple ? JSON.parse(e.target.value || '[]') : e.target.value;
            registerProps?.onChange({
              target: { value: newValue, name }
            });
            onChange?.(newValue);
          }}
        />
      )}
    </div>
  );
};

export default SelectField;
